import { Backdrop, Box, CircularProgress, Divider, Typography, Paper, Grid } from "@mui/material";
import { ColumnsType } from "antd/es/table";
import AntTable from 'antd/es/table';
import React, { useEffect, useMemo, useState } from "react";
import { Select, Badge } from 'antd';
import Table from '../../TableResizable';
import { Crypter } from "../../../Functions/Security/Crypter";
import axios from "axios";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from "../../../Services/Auth/sessionAuth";
import Utilisateur from "../../../Models/Utilisateur";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import ReformatDate from "../../../Functions/Traitement/ReformatDate";

// Charts
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';

// ─── Types internes ───────────────────────────────────────────────────────────
interface DossierRow {
    key: number;
    NumeroFinancement: string;
    CodeProduit: string;
    NomProduit: string;
    Numcli: string;
    Nomcli: string;
    DateDemande: string;
    DateVal: string;
    CODGEA: string;
    NomGestionnaire: string;
    MontantValide: number;
    TotalDecaisse: number;
    ResteADecaisser: number;
    /** Spécifique Salam : date de livraison prévue des marchandises */
    DateLivraisonPrevue?: string;
    /** Spécifique Salam : statut de la livraison */
    StatutLivraison?: string;
}

interface EcheanceRow {
    key: number;
    NumeroFinancement: string;
    NumeroEcheance: string;
    DateEcheance: string;
    MontantCapital: number;
    MontantPaye: number;
    SoldeEcheance: number;
    DatePaiement: string;
    Statut: string;
}

interface Props {
    form?: any;
    dataFindQard: any[];
    dataFindSalam?: any[];
    dataFindMourabaha?: any[];
    dataFindIjara?: any[];
}

interface ProduitConfig {
    key: string;
    label: string;
    rows: DossierRow[];
    withAmort: boolean;
    /** Titre affiché en majuscules (ex. ÉCHÉANCIER, TABLEAU LOCATIF) */
    amortTitle?: string;
    /** Nom en minuscules pour les messages (ex. échéancier, tableau locatif) */
    amortName?: string;
    /** Libellé de la colonne montant (ex. Amortissement, Loyer) */
    amountLabel?: string;
    /** Colonnes supplémentaires propres au produit (ex. livraison Salam) */
    extraCols?: ColumnsType<DossierRow>;
    color: string;
}

const CHART_COLORS = ['#2A6596', '#00875A', '#b45309', '#7c3aed'];

const tooltipStyle = {
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontSize: 12,
    backgroundColor: '#fff',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
};

// ─── Colonnes dossiers ───────────────────────────────────────────────────────
const colsDossier: ColumnsType<DossierRow> = [
    { title: 'N° Financement', dataIndex: 'NumeroFinancement', width: 155, ellipsis: true },
    { title: 'Produit',        dataIndex: 'NomProduit',        width: 140, ellipsis: true },
    { title: 'Client',         dataIndex: 'Numcli',            width: 90,  ellipsis: true },
    { title: 'Nom client',     dataIndex: 'Nomcli',            width: 130, ellipsis: true },
    { title: 'Date demande',   dataIndex: 'DateDemande',       width: 115, ellipsis: true },
    { title: 'Date validation',dataIndex: 'DateVal',           width: 115, ellipsis: true },
    { title: 'Gestionnaire',   dataIndex: 'NomGestionnaire',   width: 140, ellipsis: true },
    {
        title: 'Montant validé', dataIndex: 'MontantValide',
        width: 130, align: 'right', ellipsis: true,
        render: (v: number) => <span style={{ fontWeight: 600, color: '#04446e' }}>{formatNumber(v)}</span>
    },
    {
        title: 'Décaissé', dataIndex: 'TotalDecaisse',
        width: 120, align: 'right', ellipsis: true,
        render: (v: number) => <span style={{ color: '#c0392b' }}>{formatNumber(v)}</span>
    },
    {
        title: 'Reste à décaisser', dataIndex: 'ResteADecaisser',
        width: 140, align: 'right', ellipsis: true,
        render: (v: number) => <span style={{ color: '#00875A', fontWeight: 600 }}>{formatNumber(v)}</span>
    },
];

// ─── Colonnes supplémentaires Salam (livraison des marchandises) ─────────────
const colsSalamExtra: ColumnsType<DossierRow> = [
    { title: 'Livraison prévue', dataIndex: 'DateLivraisonPrevue', width: 125, ellipsis: true },
    {
        title: 'Statut livraison', dataIndex: 'StatutLivraison', width: 120, align: 'center',
        render: (v: string) => (
            <Badge
                color={v === 'LIVRE' || v === 'Livré' ? 'green' : v ? 'orange' : 'default'}
                text={v || '—'}
            />
        ),
    },
];

// ─── Colonnes amortissement ──────────────────────────────────────────────────
const colsAmort = (amountLabel: string): ColumnsType<EcheanceRow> => [
    { title: 'N° Éch.',        dataIndex: 'NumeroEcheance', width: 70,  align: 'center' },
    { title: 'Date échéance',  dataIndex: 'DateEcheance',   width: 115, ellipsis: true  },
    { title: amountLabel,      dataIndex: 'MontantCapital', width: 120, align: 'right',
      render: (v: number) => <span style={{ color: '#2A6596' }}>{formatNumber(v)}</span> },
    { title: 'Montant payé',   dataIndex: 'MontantPaye',    width: 115, align: 'right',
      render: (v: number) => <span style={{ color: '#00875A' }}>{formatNumber(v)}</span> },
    { title: 'Solde échéance', dataIndex: 'SoldeEcheance',  width: 115, align: 'right',
      render: (v: number) => <span style={{ color: '#c0392b' }}>{formatNumber(v)}</span> },
    { title: 'Date paiement',  dataIndex: 'DatePaiement',   width: 115, ellipsis: true  },
    {
        title: 'Statut', dataIndex: 'Statut', width: 90, align: 'center',
        render: (v: string) => (
            <Badge
                color={v === 'Payé' || v === 'PAYEE' ? 'green' : v === 'Partiel' ? 'orange' : 'red'}
                text={v || '—'}
            />
        ),
    },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const toNum = (v: any): number => {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return v;
    const s = String(v).replace(/\s/g, '').replace(/,/g, '');
    const n = Number(s);
    return Number.isNaN(n) ? 0 : n;
};

const mapDossier = (data: any, indice: number, produitField: string): DossierRow => ({
    key: indice,
    NumeroFinancement: data.NumeroFinancement ?? '',
    CodeProduit:       data[produitField] ?? '',
    NomProduit:        data.NomProduit ?? data[produitField] ?? '',
    Numcli:            data.Numcli  ?? '',
    Nomcli:            data.Nomcli  ?? '',
    DateDemande:       data.DateDemande ?? '',
    DateVal:           data.DateVal ?? '',
    CODGEA:            data.CODGEA ?? '',
    NomGestionnaire:   data.NomGestionnaire ?? data.CODGEA ?? '',
    MontantValide:     toNum(data.MontantValide ?? data.PrixVenteValide ?? 0),
    TotalDecaisse:     toNum(data.TotalDecaisse ?? 0),
    ResteADecaisser:   toNum(data.ResteADecaisser ?? 0),
    DateLivraisonPrevue: data.DateLivraisonPrevue
        ? new Date(String(data.DateLivraisonPrevue).substring(0, 10)).toLocaleDateString('fr')
        : '',
    StatutLivraison:   data.StatutLivraison ?? data.statut ?? '',
});

const mapEcheance = (d: any, i: number): EcheanceRow => ({
    key: i,
    NumeroFinancement: d.NumeroFinancement ?? '',
    NumeroEcheance:    String(d.NumeroEcheance ?? d.NumeroLoyer ?? ''),
    DateEcheance:      (d.DateEcheance ?? d.DateLoyer)
        ? new Date(String(d.DateEcheance ?? d.DateLoyer).substring(0, 10)).toLocaleDateString('fr')
        : '',
    MontantCapital:    toNum(d.MontantCapital ?? d.MontantAmortissement ?? d.Amortissement ?? d.LoyerTTC ?? 0),
    MontantPaye:       toNum(d.MontantPaye ?? 0),
    SoldeEcheance:     toNum(d.SoldeEcheance ?? d.SoldeLoyer ?? d.Solde ?? 0),
    DatePaiement:      d.DatePaiement ? ReformatDate(d.DatePaiement) : '',
    Statut:            d.Statut ?? '',
});

// ─── Charts : évolution financière par produit ────────────────────────────────
const FinancementCharts = ({ produits }: { produits: ProduitConfig[] }) => {
    const barData = useMemo(
        () =>
            produits.map((p) => ({
                name: p.label,
                Valide:   p.rows.reduce((s, r) => s + toNum(r.MontantValide), 0),
                Decaisse: p.rows.reduce((s, r) => s + toNum(r.TotalDecaisse), 0),
                Reste:    p.rows.reduce((s, r) => s + toNum(r.ResteADecaisser), 0),
            })),
        [produits],
    );

    const pieData = useMemo(
        () =>
            produits.map((p, i) => ({
                name:  p.label,
                value: p.rows.length,
                color: CHART_COLORS[i % CHART_COLORS.length],
            })),
        [produits],
    );

    const moneyTick = (v: number) => {
        if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}Md`;
        if (v >= 1_000_000)     return `${(v / 1_000_000).toFixed(1)}M`;
        if (v >= 1_000)         return `${(v / 1_000).toFixed(0)}k`;
        return String(v);
    };

    if (produits.length === 0) return null;

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Bar chart */}
            <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{
                    p: 2, borderRadius: '10px',
                    border: '1px solid #e3edf7',
                    boxShadow: '0 2px 12px rgba(4,68,110,0.06)',
                }}>
                    <Typography sx={{ fontWeight: 700, color: '#04446e', mb: 0.5 }}>
                        Évolution financière par produit
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#90a4b7', mb: 2 }}>
                        Montants validés / décaissés / restants
                    </Typography>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={moneyTick} width={60} />
                            <RTooltip
                                contentStyle={tooltipStyle}
                                formatter={(v: any) => formatNumber(Number(v))}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="Valide"   name="Validé"   fill="#2A6596" radius={[4, 4, 0, 0]} barSize={24} />
                            <Bar dataKey="Decaisse" name="Décaissé" fill="#00875A" radius={[4, 4, 0, 0]} barSize={24} />
                            <Bar dataKey="Reste"    name="Reste"    fill="#b45309" radius={[4, 4, 0, 0]} barSize={24} />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>

            {/* Pie chart */}
            <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{
                    p: 2, borderRadius: '10px', height: '100%',
                    border: '1px solid #e3edf7',
                    boxShadow: '0 2px 12px rgba(4,68,110,0.06)',
                }}>
                    <Typography sx={{ fontWeight: 700, color: '#04446e', mb: 0.5 }}>
                        Répartition des dossiers
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#90a4b7', mb: 2 }}>
                        Nombre de dossiers par produit
                    </Typography>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%" cy="50%"
                                innerRadius={50}
                                outerRadius={90}
                                paddingAngle={3}
                                label={(entry: any) => `${entry.name}: ${entry.value}`}
                            >
                                {pieData.map((p, i) => (
                                    <Cell key={i} fill={p.color} />
                                ))}
                            </Pie>
                            <RTooltip contentStyle={tooltipStyle} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>
        </Grid>
    );
};

// ─── Sous-composant : panneau d'un produit ────────────────────────────────────
interface PanelProps {
    dossiers: DossierRow[];
    withAmort: boolean;
    amortTitle: string;
    amortName: string;
    amountLabel: string;
    extraCols?: ColumnsType<DossierRow>;
    authHeader: () => string;
    user: Utilisateur;
}

const ProduitPanel = ({ dossiers, withAmort, amortTitle, amortName, amountLabel, extraCols, authHeader, user }: PanelProps) => {
    const [selectedKey, setSelectedKey]   = useState<React.Key[]>([]);
    const [amortData,   setAmortData]     = useState<EcheanceRow[]>([]);
    const [loadingAmort, setLoadingAmort] = useState(false);

    const fetchAmort = (numDossier: string) => {
        // sp_AnyGetDetailClient Option 4 : @NUMCLI transporte le NumeroFinancement
        // et renvoie 3 tables (Qard / Mourabaha / Ijara) — une seule est remplie.
        setLoadingAmort(true);
        axios({
            method: 'get', maxBodyLength: Infinity,
            url: Data.ApiUrl.GetDetailClient,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
                'CypherNumcli': Crypter.encrypt(numDossier),
                'CypherCodu': Crypter.encrypt(user.coduti),
                'CypherAgence': Crypter.encrypt(user.codage),
                'CypherOption': Crypter.encrypt('4'),
            },
        }).then(response => {
            if (response.data) {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode === 200 && res.Result) {
                    const table =
                        (Array.isArray(res.Result.Table1) && res.Result.Table1.length > 0) ? res.Result.Table1 :
                        (Array.isArray(res.Result.Table2) && res.Result.Table2.length > 0) ? res.Result.Table2 :
                        (Array.isArray(res.Result.Table3) && res.Result.Table3.length > 0) ? res.Result.Table3 : [];
                    setAmortData(table.map(mapEcheance));
                } else {
                    setAmortData([]);
                }
            }
        }).catch(console.error)
          .finally(() => setLoadingAmort(false));
    };

    const onSelect = (newKey: any) => {
        setSelectedKey(newKey);
        const row = dossiers[newKey];
        if (row) fetchAmort(row.NumeroFinancement);
    };

    const selected = selectedKey.length > 0 ? dossiers[selectedKey[0] as number] : null;

    // Colonnes du tableau de dossiers : colonnes communes + colonnes propres au produit
    const colsDossierProduit = useMemo(
        () => [...colsDossier, ...(extraCols ?? [])],
        [extraCols]
    );

    const totalsDossier = useMemo(() => ({
        valide:   dossiers.reduce((s, r) => s + toNum(r.MontantValide), 0),
        decaisse: dossiers.reduce((s, r) => s + toNum(r.TotalDecaisse), 0),
        reste:    dossiers.reduce((s, r) => s + toNum(r.ResteADecaisser), 0),
    }), [dossiers]);

    const totalsAmort = useMemo(() => ({
        capital: amortData.reduce((s, r) => s + toNum(r.MontantCapital), 0),
        paye:    amortData.reduce((s, r) => s + toNum(r.MontantPaye), 0),
        solde:   amortData.reduce((s, r) => s + toNum(r.SoldeEcheance), 0),
    }), [amortData]);

    return (
        <Box sx={{ pt: 1 }}>
            {/* Tableau dossiers */}
            <Box className="bordure1" sx={{ mb: 2, borderRadius: '8px', overflow: 'hidden' }}>
                <Box sx={{
                    px: 2, py: 0.8,
                    background: 'linear-gradient(90deg, #04446e 0%, #2A6596 100%)',
                    display: 'flex', alignItems: 'center', gap: 1,
                }}>
                    <span style={{ color: '#B0D2F7', fontWeight: 700, fontSize: 12, letterSpacing: 0.5 }}>
                        DOSSIERS DE FINANCEMENT
                    </span>
                    <Badge
                        count={dossiers.length}
                        style={{ backgroundColor: '#B0D2F7', color: '#04446e', fontWeight: 700, fontSize: 11 }}
                    />
                </Box>
                <Table
                    size="small"
                    pagination={false}
                    rowSelection={{ selectedRowKeys: selectedKey, onChange: onSelect, type: 'radio' }}
                    scroll={{ y: 160, x: 1200 }}
                    columns={colsDossierProduit}
                    dataSource={dossiers}
                    locale={{ emptyText: 'Aucun dossier de financement.' }}
                    rowClassName={(_, i) => i % 2 === 0 ? 'row-even' : ''}
                    summary={() => (
                        <AntTable.Summary fixed>
                            <AntTable.Summary.Row style={{ background: '#f0f6ff', fontWeight: 700 }}>
                                <AntTable.Summary.Cell index={0} colSpan={7}>
                                    <span style={{ color: '#04446e', fontWeight: 700 }}>
                                        TOTAL ({dossiers.length} dossier{dossiers.length > 1 ? 's' : ''})
                                    </span>
                                </AntTable.Summary.Cell>
                                <AntTable.Summary.Cell index={7} align="right">
                                    <span style={{ color: '#04446e', fontWeight: 700 }}>{formatNumber(totalsDossier.valide)}</span>
                                </AntTable.Summary.Cell>
                                <AntTable.Summary.Cell index={8} align="right">
                                    <span style={{ color: '#c0392b', fontWeight: 700 }}>{formatNumber(totalsDossier.decaisse)}</span>
                                </AntTable.Summary.Cell>
                                <AntTable.Summary.Cell index={9} align="right">
                                    <span style={{ color: '#00875A', fontWeight: 700 }}>{formatNumber(totalsDossier.reste)}</span>
                                </AntTable.Summary.Cell>
                                {(extraCols ?? []).map((_, i) => (
                                    <AntTable.Summary.Cell index={10 + i} key={i} />
                                ))}
                            </AntTable.Summary.Row>
                        </AntTable.Summary>
                    )}
                />
            </Box>

            {/* Résumé du dossier sélectionné */}
            {selected && (
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 2, px: 2, py: 1.2, mb: 2,
                    borderRadius: '8px', bgcolor: '#f0f6ff',
                    border: '1px solid #b3d4f0',
                    fontSize: 12, color: '#2d4a5e',
                }}>
                    <span><b>Dossier :</b> {selected.NumeroFinancement}</span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Client :</b> {selected.Nomcli}</span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Produit :</b> {selected.NomProduit}</span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Gestionnaire :</b> {selected.NomGestionnaire}</span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Validé :</b> <span style={{ color: '#04446e', fontWeight: 700 }}>{formatNumber(selected.MontantValide)}</span></span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Décaissé :</b> <span style={{ color: '#c0392b', fontWeight: 700 }}>{formatNumber(selected.TotalDecaisse)}</span></span>
                    <Divider orientation="vertical" flexItem />
                    <span><b>Reste :</b> <span style={{ color: '#00875A', fontWeight: 700 }}>{formatNumber(selected.ResteADecaisser)}</span></span>
                </Box>
            )}

            {/* Tableau amortissement + totaux */}
            {withAmort && (
                <Box className="bordure1" sx={{ borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                    <Box sx={{
                        px: 2, py: 0.8,
                        background: 'linear-gradient(90deg, #00875A 0%, #00a36e 100%)',
                        display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: 0.5 }}>
                            {amortTitle}
                        </span>
                        {selectedKey.length === 0 && (
                            <span style={{ color: 'rgba(255,255,255,.7)', fontSize: 11, fontStyle: 'italic' }}>
                                — Sélectionnez un dossier
                            </span>
                        )}
                        {amortData.length > 0 && (
                            <Badge
                                count={amortData.length}
                                style={{ backgroundColor: '#fff', color: '#00875A', fontWeight: 700, fontSize: 11 }}
                            />
                        )}
                    </Box>
                    <Table
                        size="small"
                        pagination={false}
                        scroll={{ y: 170, x: 700 }}
                        columns={colsAmort(amountLabel)}
                        dataSource={amortData}
                        locale={{
                            emptyText: selectedKey.length === 0
                                ? `Sélectionnez un dossier pour afficher le ${amortName}.`
                                : `Aucune échéance trouvée pour ce dossier.`
                        }}
                        summary={() =>
                            amortData.length > 0 ? (
                                <AntTable.Summary fixed>
                                    <AntTable.Summary.Row style={{ background: '#f6ffed', fontWeight: 700 }}>
                                        <AntTable.Summary.Cell index={0} colSpan={2} align="center">
                                            <span style={{ color: '#00875A', fontWeight: 700 }}>TOTAL</span>
                                        </AntTable.Summary.Cell>
                                        <AntTable.Summary.Cell index={2} align="right">
                                            <span style={{ color: '#2A6596', fontWeight: 700 }}>{formatNumber(totalsAmort.capital)}</span>
                                        </AntTable.Summary.Cell>
                                        <AntTable.Summary.Cell index={3} align="right">
                                            <span style={{ color: '#00875A', fontWeight: 700 }}>{formatNumber(totalsAmort.paye)}</span>
                                        </AntTable.Summary.Cell>
                                        <AntTable.Summary.Cell index={4} align="right">
                                            <span style={{ color: '#c0392b', fontWeight: 700 }}>{formatNumber(totalsAmort.solde)}</span>
                                        </AntTable.Summary.Cell>
                                        <AntTable.Summary.Cell index={5} colSpan={2} />
                                    </AntTable.Summary.Row>
                                </AntTable.Summary>
                            ) : null
                        }
                    />
                    <Backdrop
                        sx={{ color: '#fff', zIndex: t => t.zIndex.drawer + 1, position: 'absolute', borderRadius: '8px' }}
                        open={loadingAmort}
                    >
                        <CircularProgress color="inherit" size={32} />
                    </Backdrop>
                </Box>
            )}

            {/* Message pour les produits sans amortissement */}
            {!withAmort && selectedKey.length > 0 && (
                <Box sx={{
                    px: 2, py: 2, borderRadius: '8px',
                    border: '1px dashed #b3d4f0', bgcolor: '#f7fafd',
                    textAlign: 'center', fontSize: 12, color: '#90a4b7', fontStyle: 'italic',
                }}>
                    Le {amortName} n'est pas disponible pour ce type de financement.
                </Box>
            )}
        </Box>
    );
};

// ─── Composant principal Tab2 ─────────────────────────────────────────────────
const Tab2 = ({
    dataFindQard,
    dataFindSalam = [],
    dataFindMourabaha = [],
    dataFindIjara = [],
}: Props) => {
    const authHeader = useAuthHeader();
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

    // ── Configuration des produits ────────────────────────────────────────
    const produits: ProduitConfig[] = useMemo(() => [
        {
            key: 'qard',
            label: 'Qard',
            rows: dataFindQard.map((d, i) => mapDossier(d, i, 'CodeQard')),
            withAmort: true,
            amortTitle: 'ÉCHÉANCIER',
            amortName: 'échéancier',
            amountLabel: 'Amortissement',
            color: '#2A6596',
        },
        {
            key: 'salam',
            label: 'Salam',
            rows: dataFindSalam.map((d, i) => mapDossier(d, i, 'CodeSalam')),
            withAmort: false,
            extraCols: colsSalamExtra,
            color: '#00875A',
        },
        {
            key: 'mourabaha',
            label: 'Mourabaha',
            rows: dataFindMourabaha.map((d, i) => mapDossier(d, i, 'CodeMourabaha')),
            withAmort: true,
            amortTitle: 'ÉCHÉANCIER',
            amortName: 'échéancier',
            amountLabel: 'Amortissement',
            color: '#b45309',
        },
        {
            key: 'ijara',
            label: 'Ijara',
            rows: dataFindIjara.map((d, i) => mapDossier(d, i, 'CodeIjara')),
            withAmort: true,
            amortTitle: 'TABLEAU LOCATIF',
            amortName: 'tableau locatif',
            amountLabel: 'Loyer',
            color: '#7c3aed',
        },
    ], [dataFindQard, dataFindSalam, dataFindMourabaha, dataFindIjara]);

    const availableProduits = produits.filter(p => p.rows.length > 0);

    const [selectedProduit, setSelectedProduit] = useState<string>('');

    useEffect(() => {
        if (availableProduits.length === 0) {
            if (selectedProduit !== '') setSelectedProduit('');
            return;
        }
        if (!availableProduits.find(p => p.key === selectedProduit)) {
            setSelectedProduit(availableProduits[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataFindQard.length, dataFindSalam.length, dataFindMourabaha.length, dataFindIjara.length, selectedProduit]);

    const currentProduit = availableProduits.find(p => p.key === selectedProduit);

    return (
        <div className="tabItem">
            <Box width="85%" position="absolute">

                {/* ── Sélecteur de produit ──────────────────────────────── */}
                <Box sx={{
                    mb: 1.5, p: 1.5, borderRadius: 2,
                    bgcolor: '#fff',
                    border: '1px solid #e3e8ee',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#04446e' }}>
                        Produit de financement :
                    </Typography>

                    <Select
                        value={selectedProduit || undefined}
                        onChange={(v) => setSelectedProduit(v)}
                        style={{ width: 320 }}
                        size="large"
                        placeholder="Choisir un produit..."
                        disabled={availableProduits.length === 0}
                        options={availableProduits.map(p => ({
                            value: p.key,
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                        display: 'inline-block',
                                        width: 8, height: 8, borderRadius: '50%',
                                        backgroundColor: p.color,
                                    }} />
                                    <span style={{ fontWeight: 600 }}>{p.label}</span>
                                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                                        ({p.rows.length})
                                    </span>
                                </span>
                            ),
                        }))}
                    />

                    {currentProduit && (
                        <Typography sx={{ fontSize: 12, color: '#666', ml: 'auto' }}>
                            {currentProduit.rows.length} dossier(s) trouvé(s)
                        </Typography>
                    )}
                </Box>

                {/* ── Charts ─────────────────────────────────────────────── */}
                <FinancementCharts produits={availableProduits} />

                {/* ── Panneau du produit sélectionné ─────────────────────── */}
                {currentProduit ? (
                    <ProduitPanel
                        key={currentProduit.key}
                        dossiers={currentProduit.rows}
                        withAmort={currentProduit.withAmort}
                        amortTitle={currentProduit.amortTitle ?? 'ÉCHÉANCIER'}
                        amortName={currentProduit.amortName ?? 'échéancier'}
                        amountLabel={currentProduit.amountLabel ?? 'Montant échéance'}
                        extraCols={currentProduit.extraCols}
                        authHeader={authHeader}
                        user={user}
                    />
                ) : (
                    <Box sx={{
                        p: 4, textAlign: 'center', borderRadius: 2,
                        border: '1px dashed #cfd8dc', bgcolor: '#fafbfc',
                        color: '#90a4b7', fontStyle: 'italic', fontSize: 13,
                    }}>
                        Aucun produit de financement disponible pour ce client.
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default Tab2;
